import moment from 'moment-timezone';
import googleMapsHelper from './google-maps-helper';
import { groupAndFormatCommonHours } from './group-and-format-common-hours';
import { operationalHoursError } from './constants';

const timeZones = {
  AST: 'America/Puerto_Rico',
  EST: 'America/New_York',
  EDT: 'America/New_York',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  AKST: 'America/Anchorage',
  AKDT: 'America/Anchorage',
  HAST: 'Pacific/Honolulu',
  HADT: 'Pacific/Honolulu',
};

const validStoreOperationalKeys = [
  'mondayHrs',
  'tuesdayHrs',
  'wednesdayHrs',
  'thursdayHrs',
  'fridayHrs',
  'saturdayHrs',
  'sundayHrs',
  'monToFriHrs',
  'startHr',
  'endHr',
  'open24Hours',
  'closed'
];

class StoreLocation {
  constructor(location) {
    this.address = location.address || {};
    this.distance = location.distance || {};
    this.id = location.id;
    this.name = location.displayName;
    this.phone = location.phone;
    this.photoService = (location.services || []).find(service => service.name === 'PHOTO_CENTER') || {};
    this.rawOperationalHours = location.operationalHours || {};
    this.timeZone = location.timeZone;
  }

  get rawPhotoOperationalHours() {
    const { operationalHours = {} } = this.photoService;

    if (!Object.keys(operationalHours).length) {
      return this.rawOperationalHours;
    }

    return operationalHours;
  }

  get storeOperationalHours() {
    return this.groupCommonHours(this.rawOperationalHours);
  }

  get photoOperationalHours() {
    return this.groupCommonHours(this.rawPhotoOperationalHours);
  }

  get storeOpenUntil() {
    return this.openUntil(this.rawOperationalHours);
  }

  get photoOpenUntil() {
    return this.openUntil(this.rawPhotoOperationalHours);
  }

  get googleMapDirectionsUrl() {
    return googleMapsHelper.directionsUrl(this.address);
  }

  get longTimeZone() {
    return timeZones[this.timeZone] || this.timeZone;
  }

  validOperationalHours(operationalHours) {
    if (!operationalHours || Object.keys(operationalHours).length < 2) {
      return false;
    }

    const keys = [];

    Object.keys(operationalHours).forEach(key => {
      keys.push(key, ...Object.keys(operationalHours[key]));
    });

    return keys.every(key => validStoreOperationalKeys.includes(key));
  }

  formatTime(timestamp) {
    const time = moment(timestamp, 'HH:mm:ss');

    if (time.minute()) {
      return time.format('h:mma');
    }

    return time.format('ha');
  }

  groupCommonHours(operationalHours) {
    if (!this.validOperationalHours(operationalHours)) {
      return operationalHoursError;
    }

    return groupAndFormatCommonHours(operationalHours);
  }

  static hoursForDay(date, operationalHours) {
    switch (date.day()) {
      case 0:
        return operationalHours.sundayHrs || operationalHours.monToFriHrs;
      case 1:
        return operationalHours.mondayHrs || operationalHours.monToFriHrs;
      case 2:
        return operationalHours.tuesdayHrs || operationalHours.monToFriHrs;
      case 3:
        return operationalHours.wednesdayHrs || operationalHours.monToFriHrs;
      case 4:
        return operationalHours.thursdayHrs || operationalHours.monToFriHrs;
      case 5:
        return operationalHours.fridayHrs || operationalHours.monToFriHrs;
      case 6:
        return operationalHours.saturdayHrs;
    }
  }

  openUntil(operationalHours) {
    if (!this.validOperationalHours(operationalHours)) {
      return operationalHoursError;
    }

    const timeZone = this.longTimeZone;
    const nowDate = moment.tz(timeZone);
    const today = StoreLocation.hoursForDay(nowDate, operationalHours);
    const startDate = moment.tz(today.startHr, 'HH:mm:ss', timeZone);
    const endDate = moment.tz(today.endHr, 'HH:mm:ss', timeZone);

    // Add 1 day to endDate if endHr is tomorrow (eg 1am)
    if (endDate.isBefore(startDate)) {
      endDate.add(1, 'days');
    }

    const openUntilText = (() => {
      if (today.closed) return;

      if (operationalHours.open24Hours) {
        return 'Open 24 hours';
      }

      if (nowDate.isBetween(startDate, endDate)) {
        return `Open until ${this.formatTime(today.endHr)}`;
      }

      if (nowDate.isBefore(startDate)) {
        return `Opens at ${this.formatTime(today.startHr)}`;
      }

      const tomorrow = StoreLocation.hoursForDay(nowDate.clone().add(1, 'days'), operationalHours);
      if (tomorrow.startHr) {
        return `Opens tomorrow at ${this.formatTime(tomorrow.startHr)}`;
      }
    })();

    return openUntilText;
  }
}

export default StoreLocation;
